import { Component, Input, ElementRef, ViewChild, AfterViewInit, HostListener, Output, EventEmitter, OnDestroy } from '@angular/core';
import { Chart, registerables } from 'chart.js';
import * as _ from 'lodash';
import { NgClass, NgStyle, NgFor } from '@angular/common';

Chart.register(...registerables);

@Component({
  selector: 'app-litmus-chart-beta',
  imports: [NgClass, NgStyle, NgFor],
  templateUrl: './litmus-chart-beta.component.html',
  styleUrl: './litmus-chart-beta.component.scss'
})
export class LitmusChartBetaComponent implements AfterViewInit, OnDestroy {
  @ViewChild('chartContainer', { static: false }) chartContainer!: ElementRef;
  @Output() onGraphDataPointClick: any = new EventEmitter<any>();
  private mainChartInstance: Chart | null = null;
  private axisChartInstance: Chart | null = null;
  private resizeTimeout: any;
  isCreatingCharts: boolean = false;

  selectedDatasetIndex: number | null = null;

  public mainChartId: string = 'main-chart-' + Math.random().toString(36).substring(2, 9);
  public axisChartId: string = 'axis-chart-' + Math.random().toString(36).substring(2, 9);
  public boxId: string = 'box-' + Math.random().toString(36).substring(2, 9);

  tooltipX: any = '';
  tooltipY: any = '';
  tooltipVisible: any = '';

  @Input('chartData')
  set chartData(value) {
    this.selectedDatasetIndex = null;
    this.data = { ...value };
    this.labels = value?.labels;
    this.dataDeepCopy = { ...value };
    if (this.mainChartInstance && this.axisChartInstance) {
      this.createCharts();
    }
  }
  @Input('chartConfigs')
  set chartConfigs(value) {
    this.chartType = (value?.chartType !== undefined) ? value.chartType : 'bar';
    if (this.chartType === 'line') {
      this.legendTop = '-59px';
    }
    this.useCustomConfig = (value?.useCustomConfig !== undefined) ? value.useCustomConfig : false;
    this.customMin = (value?.min !== undefined) ? value.min : null;
    this.customMax = (value?.max !== undefined) ? value.max : null;
    this.customStepSize = (value?.stepSize !== undefined) ? value.stepSize : null;
    this.stacked = (value.stacked === false) ? false : true
    this.showPercentage = (value.showPercentage === true) ? true : false;
    this.showTotalValue = (value.showTotalValue === true) ? true : false;
    this.totalValueLabel = (value.totalValueLabel !== undefined) ? value.totalValueLabel : 'Total';
    this.yText = (value.yText !== undefined) ? value.yText : '';
    this.xText = (value.xText !== undefined) ? value.xText : '';
    this.minBarWidth = (value.minBarWidth !== undefined) ? value.minBarWidth : 142.8;
  }

  chartType: string = 'bar';
  useCustomConfig: boolean = false;
  customMin: any = null;
  customMax: any = null;
  customStepSize: any = null;
  stacked: boolean = true;
  showPercentage: boolean = false;
  yText: string = '';
  xText: string = '';
  data: any = {};
  dataDeepCopy: any = {};
  labels: any = [];
  event: any = {};
  previousTime: any = {};
  minBarWidth: number = 142.8;
  showTotalValue: boolean = false;
  totalValueLabel: string = 'Total';
  pointAnimationEnabled: boolean = false;
  ngAfterViewInit(): void {
    this.createCharts();
  }

  ngOnDestroy(): void {
    if (this.mainChartInstance) {
      this.mainChartInstance.destroy();
      this.mainChartInstance = null;
    }
    if (this.axisChartInstance) {
      this.axisChartInstance.destroy();
      this.axisChartInstance = null;
    }
    clearTimeout(this.resizeTimeout);
  }

  @HostListener('window:resize')
  onResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.createCharts();
    }, 200);
  }

  calcWidth(): any {
    const minWidth = '100%';
    let columnWidth = this.minBarWidth;
    if (this.stacked === false && this.chartType === 'bar') {
      if (this.data.datasets.length > 0) {
        columnWidth = this.data.datasets.length * (this.minBarWidth + 5);
      }
    }
    const calculatedWidth = (this.data.labels ? this.data.labels.length : 0) * columnWidth + 'px';
    return `max(${minWidth}, ${calculatedWidth})`;
  }

  setActiveBar(index) {
    const chartDataset = _.cloneDeep(this.data?.datasets);
    chartDataset.forEach(item => {
      const baseColor = item?.backgroundColor;
      const dataLength = item.data.length;
      const backgroundColorArray: any = [];
      for (let i = 0; i < dataLength; i++) {
        if (i === index) {
          const semiTransparentColor = baseColor;
          backgroundColorArray.push(semiTransparentColor);
        } else {
          backgroundColorArray.push(baseColor + '80');
        }
      }
      item.backgroundColor = backgroundColorArray;
    });
    this.dataDeepCopy.datasets = chartDataset;
    this.mainChartInstance?.update();
    this.selectedDatasetIndex = null;
  }

  createCharts(): void {
    if (this.isCreatingCharts) return;
    this.isCreatingCharts = true;
    if (this.mainChartInstance) {
      this.mainChartInstance.destroy();
      this.mainChartInstance = null;
    }
    if (this.axisChartInstance) {
      this.axisChartInstance.destroy();
      this.axisChartInstance = null;
    }
    if(this.chartType == 'line' && this.data.datasets[0].pointBorderWidth == 0 && this.labels?.length > 1) {
      this.pointAnimationEnabled = true;
    } else if(this.chartType == 'line' && this.labels?.length == 1) {
      if(this.data.datasets[0].pointBorderWidth == 0) {
        this.data.datasets[0].pointBorderWidth = 3;
        this.data.datasets[0].pointRadius = 3;
      }
      this.pointAnimationEnabled = false;

    }
    const { MainChartBottomPadding, AxisChartBottomPadding } = this.getBottomPadding();

    setTimeout(() => {
      try {
        this.calculateInitailData();
        this.renderMainChart(MainChartBottomPadding);
        this.renderAxisChart(AxisChartBottomPadding);
        this.registerEventListeners();
      } catch (error) {
        console.error('Error creating charts:', error);
      } finally {
        this.isCreatingCharts = false;
      }
    }, 100);
  }

  largestValue: number = 0;
  stepSize: number = 0;
  calculateInitailData() {
    this.largestValue = this.getLargestValue();
    this.stepSize = (this.useCustomConfig && this.customStepSize) ? this.customStepSize : null;
    if (!this.stepSize) {
      this.stepSize = (this.showPercentage) ? 100 / 5 : this.largestValue / 5;
    }
  }

  getBottomPadding() {
    let MainChartBottomPadding = 130;
    let AxisChartBottomPadding = 137;
    const div = document.getElementById(this.boxId);
    if (div) {
      const isScrollable = div.scrollWidth > div.clientWidth;
      if (isScrollable) {
        AxisChartBottomPadding = 140;
      }
    }
    return { MainChartBottomPadding, AxisChartBottomPadding };
  }



  renderMainChart(paddingBottom: number) {
    const mainChartConfig: any = {
      type: this.chartType,
      data: this.dataDeepCopy,
      plugins: [this.getLablePlugin()],
      options: {
        maintainAspectRatio: false,
        responsive: true,
        layout: {
          padding: { bottom: paddingBottom, top: 12, left: 0, right: (this.chartType === 'line') ? 10 : 0 },
        },
        scales: this.getScalesConfig(),
        onClick: (event) => {
          if (this.mainChartInstance) {
            const activePoints = this.mainChartInstance.getElementsAtEventForMode(
              event,
              'nearest',
              { intersect: true },
              false
            );
            if (activePoints.length) {
              const clickedIndex = activePoints[0].index;
              const datasetIndex = activePoints[0].datasetIndex;
              this.setActiveBar(clickedIndex);
              const barChartPointData = {
                data: this.dataDeepCopy,
                clickedIndex: clickedIndex,
                datasetIndex: datasetIndex,
              };
              this.onGraphDataPointClick.emit(barChartPointData);
            }
          }
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: false,
          },
        },
        hover: {
          mode: null
        },
      },
    };
    this.mainChartInstance = new Chart(this.mainChartId, mainChartConfig);
  }


  
  renderAxisChart(paddingBottom: number) {
    const axisChartConfig: any = {
      type: this.chartType,
      data: {
        labels: this.dataDeepCopy?.labels || [],
        datasets: (this.dataDeepCopy?.datasets && this.dataDeepCopy?.datasets?.length > 0) ? _.map(Helpers.deepClone(this.dataDeepCopy?.datasets), (dataset: any) => {
          dataset.hidden = true;
          return dataset;
        }) : [],
      },
      plugins: [this.getLablePlugin()],
      options: {
        maintainAspectRatio: false,
        responsive: true,
        layout: {
          padding: { bottom: paddingBottom, top: 0, left: 0, right: 0 },
        },
        scales: {
          x: {
            stacked: this.stacked,
            ticks: {
              display: false,
              maxRotation: 90,
              minRotation: 90,
            },
            grid: {
              drawTicks: false,
              drawBorder: false,
              display: false,
            },
            border: {
              display: false,
            },
          },
          y: {
            stacked: this.stacked,
            beginAtZero: true,
            afterFit: (ctx: any) => {
              ctx.width = 55;
            },
            grid: {
              drawTicks: false,
              drawBorder: false,
              display: true,
              color: 'rgba(0, 0, 0, 0.8)',
              lineWidth: 0.5,
            },
            border: {
              display: false,
            },
            ticks: {
              stepSize: this.stepSize,
              font: {
                size: 14,
                family: 'Asap',
              },
              count: 6,
              callback: (value: string | number) => {
                const numericValue = typeof value === 'number' ? value : Number(value);
                if (this.showPercentage) {
                  return numericValue + '%';
                } else {
                  return this.formatYAxisLabel(numericValue);
                }
              },
              padding: 5,
            },
            title: {
              display: true,
              font: {
                size: 14,
                family: 'Asap',
              },
              text: this.showPercentage ? '% ' + this.yText : this.yText,
              padding: {
                top: 0,
                bottom: 40,
              },
            },
            min: (this.useCustomConfig && this.customMin) ? this.customMin : 0,
            max: (this.useCustomConfig && this.customMax) ? this.customMax : this.largestValue,
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            enabled: false,
          },
        },
        hover: {
          mode: null
        },
      },
    };
    this.axisChartInstance = new Chart(this.axisChartId, axisChartConfig);
    // this.axisChartInstance.data.datasets.forEach((dataset, index) => {
    //   this.axisChartInstance?.hide(index);
    // });
  }
  
  getScalesConfig() {
    let scalesConfig: any = {
      x: {
        stacked: this.stacked, 
        ticks: {
          display: false,
          autoSkip: false,
          maxRotation: 90,
          minRotation: 90,
        },
        border: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        stacked: this.stacked,
        ticks: {
          maxRotation: 90,
          minRotation: 90,
          display: false,
          stepSize: this.stepSize,
          count: 6,
        },
        border: {
          display: false,
        },
        min: (this.useCustomConfig && this.customMin) ? this.customMin : 0,
        max: (this.useCustomConfig && this.customMax) ? this.customMax : this.largestValue,
      },
    }
    if(this.labels?.length == 1 && this.chartType == 'line') {
      scalesConfig.x.offset = true;
    }
    return scalesConfig;
  }
  prevInside: boolean = false;
  registerEventListeners() {
    const canvas = document.getElementById(this.mainChartId) as HTMLCanvasElement;
    if (canvas) {
      canvas.addEventListener('mousemove', (event) => {
        const points = this.mainChartInstance?.getElementsAtEventForMode(
          event,
          'nearest',
          { intersect: true },
          false
        );
        canvas.style.cursor = points?.length ? 'pointer' : 'default';
      });
    }
    if (this.mainChartInstance) {

      this.mainChartInstance.canvas.addEventListener('mouseenter', (event) => {
        if (this.mainChartInstance &&  this.pointAnimationEnabled) {
          this.mainChartInstance.data.datasets.forEach((dataset: any) => {
            dataset.pointRadius = 3;
            dataset.pointBorderWidth = 3;
            dataset.pointHoverRadius = 4;
            dataset.pointHoverBorderWidth = 4;
          });
          this.mainChartInstance.update();
        }
      });
      this.mainChartInstance.canvas.addEventListener('mouseleave', (event) => {
        if (this.mainChartInstance && this.pointAnimationEnabled) {
          this.mainChartInstance.data.datasets.forEach((dataset: any) => {
            dataset.pointRadius = 0;
            dataset.pointBorderWidth = 0;
            dataset.pointHoverRadius = 0;
            dataset.pointHoverBorderWidth = 0;
          });
          this.mainChartInstance.update();
        }
      });
      this.mainChartInstance.canvas.addEventListener('mousemove', (event) => {
        if (this.mainChartInstance) {
          const chartArea = this.mainChartInstance.chartArea;
          const mouseX = event.offsetX;
          const mouseY = event.offsetY;
          const intersectedPoints = this.mainChartInstance.getElementsAtEventForMode(
            event,
            'nearest',
            { intersect: true },
            false
          );
          if (intersectedPoints.length > 0 && mouseX >= chartArea.left && mouseX <= chartArea.right && mouseY >= chartArea.top && mouseY <= chartArea.bottom) {
            const nearestIndex = Math.floor((mouseX - chartArea.left) / (chartArea.right - chartArea.left) * this.labels.length);
            if (nearestIndex >= 0 && nearestIndex < this.labels.length) {
              const visibleDataPoints = this.data.datasets
                .map((dataset, datasetIndex) => {
                  if (this.mainChartInstance) {
                    const meta = this.mainChartInstance.getDatasetMeta(datasetIndex);
                    return meta.hidden ? null : { label: dataset.label, value: dataset.data[nearestIndex] };
                  } return;
                })
                .filter(dataPoint => dataPoint !== null);
              const obj = {
                index: nearestIndex,
                time: new Date().getTime(),
                label: this.labels[nearestIndex],
                dataPoints: visibleDataPoints
              };
              this.event = obj;
              const sumOfDataPoints = visibleDataPoints.reduce((acc: any, curr: any) => {
                if (typeof curr.value === 'number') {
                  return acc + curr.value;
                } else if (typeof curr.value === 'string' && !isNaN(curr.value)) {
                  return acc + Number(curr.value);
                }
                return acc;
              }, 0);
              if (this.showTotalValue && !this.showPercentage && this.chartType !== 'line') {
                this.event.dataPoints.push({
                  label: (this.totalValueLabel) ? this.totalValueLabel : 'Total',
                  value: sumOfDataPoints
                });
              }
              this.tooltipVisible = 'block';
            }
          } else {
            this.tooltipVisible = 'none';
          }
        }
      });
    }
  }

  private formatYAxisLabel(value: number): string {
    if (value >= 1e6) {
      return (value / 1e6).toFixed(1) + 'M';
    } else if (value >= 1e3) {
      return (value / 1e3).toFixed(1) + 'k';
    } else {
      return value.toString();
    }
  }

  getLargestValue() {
    let largestValue = 0;
    for (let i = 0; i < this.labels.length; i++) {
      let datasetValueSum = 0;
      this.data.datasets.forEach((dataset: any) => {
        let datasetValue = Number(dataset?.data[i] || 0);
        if (this.stacked) {
          datasetValueSum = datasetValueSum + datasetValue;
        } else if (datasetValue > datasetValueSum) {
          datasetValueSum = datasetValue;
        }
      });
      if (datasetValueSum > largestValue) {
        largestValue = datasetValueSum;
      }
    }
    return Math.ceil(largestValue / 5) * 5;
  }

  legendTop: string = '-90px';
  getLablePlugin() {
    return {
      id: 'customLabels',
      afterDraw: (chart, args, options) => {
        const ctx = chart.ctx;
        const xAxis = chart.scales.x;
        const chartArea = chart.chartArea;
        ctx.save();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.font = '14px Asap';
        ctx.fillStyle = chart.canvas.id.includes('axis-chart') ? '#FFFFFF' : '#000000';
        const labelY = chartArea.bottom + 10;
        if (!chart.canvas.id.includes('axis-chart')) {
          const el: any = document.getElementById(this.axisChartId);
          if (el) {
            el.style.height = chart.height + 'px';
          }
        } else {
          const { MainChartBottomPadding, AxisChartBottomPadding } = this.getBottomPadding();
          if (chart.options.layout.padding.bottom !== AxisChartBottomPadding) {
            chart.options.layout.padding.bottom = AxisChartBottomPadding;
            chart.update();
          }

        }
        const { MainChartBottomPadding, AxisChartBottomPadding } = this.getBottomPadding();
        let paddingBottom: any = AxisChartBottomPadding;
        chart.data.labels.forEach((label, i) => {
          const x = xAxis.getPixelForValue(i);
          const labelText = String(label);
          const textMetrics = ctx.measureText(labelText);
          const labelWidth = textMetrics.width;
          const labelHeight = textMetrics.actualBoundingBoxAscent + textMetrics.actualBoundingBoxDescent;
          const rotationAngle = -90;

          if (this.chartType === 'line') {
            // Rotated label rendering
            ctx.save();
            const yPosition = labelY + (labelHeight / 2) + 10;
            ctx.translate(x - 10, labelY + (labelHeight / 2));
            ctx.rotate((rotationAngle * Math.PI) / 180);
            ctx.textAlign = 'center';
            ctx.textBaseline = 'top';
            const lenegedPosition = (chart.height - (yPosition + labelWidth)) + 10
            // For rotated labels, we might need to adjust positioning
            const rotatedY = rotationAngle < -30 ? 5 : 0;
            ctx.fillText(labelText, -30, rotatedY);
            // this.legendTop = '-' + lenegedPosition + 'px';
            this.legendTop = '-59px';
            ctx.restore();
          } else {
            // Non-rotated label rendering with text wrapping
            const maxCharsPerLine = 16;
            const words = labelText.split(' ');
            const lines: any = [];
            let currentLine = '';

            words.forEach((word) => {
              if ((currentLine + word).length > maxCharsPerLine) {
                lines.push(currentLine.trim());
                currentLine = word + ' ';
              } else {
                currentLine += word + ' ';
              }
            });
            if (currentLine) {
              lines.push(currentLine.trim());
            }
            let totalHeight = 0;
            lines.forEach((line, lineIndex) => {
              totalHeight = labelY + lineIndex * 18;
              ctx.fillText(line, x, labelY + lineIndex * 18);
            });
            const lenegedPosition = (chart.height - (totalHeight + labelHeight)) - 20
            this.legendTop = '-' + lenegedPosition + 'px';
          }
        });
        ctx.restore();
      },
    }
  }



  toggleDatasetVisibility(index: number) {
    if (this.data.datasets.length === 1) return;
    if (this.selectedDatasetIndex === index) {
      this.selectedDatasetIndex = null;
    } else {
      this.selectedDatasetIndex = index;
    }
    const chart = this.mainChartInstance;
    if (!chart) return;
    const datasetMeta = chart.getDatasetMeta(index);
    const isVisible = datasetMeta.hidden === null || datasetMeta.hidden === false;
    const anyHidden = chart.data.datasets.some((_, i) => {
      const meta = chart.getDatasetMeta(i);
      return meta.hidden === true;
    });
    if (isVisible && anyHidden) {
      chart.data.datasets.forEach((_, i) => {
        const meta = chart.getDatasetMeta(i);
        if (i !== index) {
          meta.hidden = false;
        }
      });
    } else {
      chart.data.datasets.forEach((_, i) => {
        const meta = chart.getDatasetMeta(i);
        meta.hidden = true;
      });
      datasetMeta.hidden = false;
    }
    chart.update();
  }

  isAllVisible() {
    if (!this.mainChartInstance) return false;
    return this.mainChartInstance.data.datasets.every((_, i) => {
      const meta = this.mainChartInstance!.getDatasetMeta(i);
      return meta.hidden === false || meta.hidden === null;
    });
  }

  isAnyVisible() {
    if (!this.mainChartInstance) return false;
    return this.mainChartInstance.data.datasets.some((_, i) => {
      const meta = this.mainChartInstance!.getDatasetMeta(i);
      return meta.hidden === false || meta.hidden === null;
    });
  }

  isSelected(index: number) {
    if (!this.mainChartInstance) return false;
    const meta = this.mainChartInstance.getDatasetMeta(index);
    return meta.hidden === false || meta.hidden === null;
  }

  isNoneSelected() {
    if (!this.mainChartInstance) return false;
    return !this.mainChartInstance.data.datasets.some((_, i) => {
      const meta = this.mainChartInstance!.getDatasetMeta(i);
      return meta.hidden === true;
    });
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    const containerEl = this.chartContainer?.nativeElement;
    if (!containerEl) return;
    const rect = containerEl.getBoundingClientRect();
    const relativeX = event.clientX - rect.left;
    const relativeY = event.clientY - rect.top;
    this.tooltipX = relativeX >= rect.width / 2 ? relativeX - 230 : relativeX + 15;
    this.tooltipY = relativeY + 15;
    if (this.previousTime !== this.event?.time && this.event?.time) {
      this.previousTime = this.event?.time;
      this.tooltipVisible = 'block';
    } else {
      this.tooltipVisible = 'none';
    }
  }
}
